import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Icons, TextArea, Typography, TypographyType } from '../../../../components';
import { useClickOutside, useGetUserPermissions, useSimActions } from '../../../../hooks';
import { ComponentSize, ProspectCategory, TextColor } from '../../../../types';
import HoverableContent from './HoverableContent';

interface SimulationDetailsNotesProps {
  isDraft: boolean;
  notes: string;
  personaId: string;
  prospectCategory: ProspectCategory;
  prospectUserId: string;
}

const SimulationDetailsNotes = ({
  isDraft,
  notes,
  personaId,
  prospectCategory,
  prospectUserId,
}: SimulationDetailsNotesProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notesInput, setNotesInput] = useState(notes);

  const { getCanUserEditSim } = useGetUserPermissions();
  const canUserEditSim = getCanUserEditSim(prospectCategory, isDraft, prospectUserId);

  // Ref for tracking the simulation ID when editing starts.
  // Used to ensure notes are applied to the correct simulation even if it changes during editing,
  // (ie. opening another simulation with changes applied to the current simulation).
  const personaIdToEditRef = useRef<string>(personaId);
  // Ref for handling click outside events
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { runEdit } = useSimActions();

  // Update the ref with the current personaId when editing starts.
  const onEdit = useCallback(() => {
    personaIdToEditRef.current = personaId;
    setIsEditing(true);
  }, [personaId]);

  const onSubmit = useCallback(() => {
    if (notesInput === notes) {
      // If the notes are the same, don't submit the edit.
      setIsEditing(false);
    } else {
      // Otherwise, submit the edit.
      runEdit({
        payload: { id: personaIdToEditRef.current, notes: notesInput },
        onSuccess: () => setIsEditing(false),
      });
    }
  }, [notesInput, notes, runEdit]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  // When the personaId changes, reset the editing state and notes input.
  useEffect(() => {
    setIsEditing(false);
    setNotesInput(notes);
  }, [personaId, notes]);

  useClickOutside(inputRef, onSubmit);

  return (
    <div className="flex flex-col gap-1">
      <HoverableContent
        content={
          <div className="flex w-full flex-col gap-1">
            <Typography color={TextColor.SECONDARY} type={TypographyType.X_SMALL}>
              Notes
            </Typography>
            {!isEditing && <Typography type={TypographyType.X_SMALL}>{notesInput}</Typography>}
          </div>
        }
        icon={Icon.EDIT}
        tooltip="Edit notes"
        onClick={onEdit}
        disabled={!canUserEditSim || isEditing}
        showButton={!notesInput}
      />
      {isEditing && (
        <TextArea
          ref={inputRef}
          endElement={
            <div className="absolute bottom-1 right-1">
              <Icons icon={Icon.CORNER_DOWN_LEFT} onClick={onSubmit} />
            </div>
          }
          size={ComponentSize.X_SMALL}
          value={notesInput}
          onChange={(e) => setNotesInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
      )}
    </div>
  );
};

export default SimulationDetailsNotes;
